<template>
  <div class="notification-action-panel">
    <div class="notification-action-panel-section">
      <div class="notification-action-panel-section-label">
        <i class="fa-solid fa-satellite-dish"></i>
        Canais de Notificação
      </div>
      <div class="notification-action-panel-channels">
        <button
          v-for="channel in channelTypes"
          :key="channel.key"
          type="button"
          class="notification-action-panel-channel-btn"
          :class="{ 'is-active': action.channels[channel.key] && action.channels[channel.key].enabled }"
          @click="toggleChannel(channel.key)"
        >
          <i :class="channel.icon"></i>
          {{ channel.label }}
        </button>
      </div>
    </div>

    <div v-if="hasAnyChannelEnabled" class="notification-action-panel-section">
      <div class="notification-action-panel-section-label">
        <i class="fa-solid fa-users"></i>
        Grupos Notificados
      </div>
      <div
        v-for="channel in activeChannels"
        :key="channel.key"
        class="notification-action-panel-group-row"
      >
        <div class="notification-action-panel-group-name">
          <i :class="channel.icon"></i>
          {{ channel.label }}
        </div>
        <div class="notification-action-panel-group-pills">
          <button
            type="button"
            class="notification-action-panel-pill notification-action-panel-pill-all"
            :class="{ 'is-selected': action.channels[channel.key].notifyAll }"
            @click="toggleNotifyAll(channel.key)"
          >
            Todos
          </button>
          <button
            v-for="group in groupsAvailable"
            :key="group.key"
            type="button"
            class="notification-action-panel-pill"
            :class="{
              'is-selected':
                !action.channels[channel.key].notifyAll &&
                action.channels[channel.key].groups.includes(group.key),
            }"
            @click="toggleGroup(channel.key, group.key)"
          >
            {{ group.label }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="action.hasAlerts" class="notification-action-panel-section">
      <div class="notification-action-panel-section-label">
        <i class="fa-solid fa-clock"></i>
        Alertas Antecipados
        <span class="notification-action-panel-required">*</span>
      </div>
      <NotificationAlertSchedule v-model="action.alerts" required />
    </div>

    <div v-if="hasAnyChannelEnabled" class="notification-action-panel-section">
      <div class="notification-action-panel-section-label">
        <i class="fa-solid fa-envelope-open-text"></i>
        Mensagem
      </div>
      <NotificationTemplateEditor
        v-model="action.template"
        @open-params="$emit('open-params')"
        @save="$emit('save')"
      />
    </div>

    <div v-if="!hasAnyChannelEnabled" class="notification-action-panel-empty">
      <i class="fa-solid fa-satellite-dish"></i>
      <span>Ative ao menos um canal para configurar esta notificação.</span>
    </div>
  </div>
</template>

<script>
import NotificationAlertSchedule from "./NotificationAlertSchedule.vue";
import NotificationTemplateEditor from "./NotificationTemplateEditor.vue";

export default {
  name: "NotificationActionPanel",
  components: { NotificationAlertSchedule, NotificationTemplateEditor },
  props: {
    action: {
      type: Object,
      required: true,
    },
    channelTypes: {
      type: Array,
      required: true,
    },
    groupsAvailable: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    hasAnyChannelEnabled() {
      return this.channelTypes.some(
        (ch) => this.action.channels[ch.key] && this.action.channels[ch.key].enabled
      );
    },
    activeChannels() {
      return this.channelTypes.filter(
        (ch) => this.action.channels[ch.key] && this.action.channels[ch.key].enabled
      );
    },
  },
  methods: {
    toggleChannel(channelKey) {
      const ch = this.action.channels[channelKey];
      if (!ch) return;
      ch.enabled = !ch.enabled;
      this.$emit("change");
    },
    toggleNotifyAll(channelKey) {
      const ch = this.action.channels[channelKey];
      if (!ch) return;
      ch.notifyAll = !ch.notifyAll;
      if (ch.notifyAll) ch.groups = [];
      this.$emit("save");
    },
    toggleGroup(channelKey, groupKey) {
      const ch = this.action.channels[channelKey];
      if (!ch) return;
      if (ch.notifyAll) { ch.notifyAll = false; ch.groups = []; }
      const idx = ch.groups.indexOf(groupKey);
      if (idx === -1) ch.groups.push(groupKey);
      else ch.groups.splice(idx, 1);
      this.$emit("save");
    },
  },
};
</script>

<style scoped>
.notification-action-panel {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.notification-action-panel-section {
  padding: 16px 0;
  border-bottom: 1px solid var(--border-subtle);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.notification-action-panel-section:last-child {
  border-bottom: none;
}

.notification-action-panel-section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.notification-action-panel-section-label i {
  font-size: 11px;
}

.notification-action-panel-required {
  color: var(--color-danger);
}

.notification-action-panel-channels {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.notification-action-panel-channel-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 11px;
  border-radius: 999px;
  border: 1px solid #e2e5e9;
  background: #f6f8fa;
  font-size: 12px;
  color: #57606a;
  cursor: pointer;
  transition: 0.15s ease;
}

.notification-action-panel-channel-btn i {
  font-size: 11px;
}

.notification-action-panel-channel-btn.is-active {
  background: #eaf1f8;
  border-color: #a5bed6;
  color: #577696;
  font-weight: 500;
}

.notification-action-panel-channel-btn.is-active i {
  color: #577696;
}

.notification-action-panel-group-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  background: var(--bg-elevated);
  border-radius: 10px;
  border: 1px solid var(--border-subtle);
}

.notification-action-panel-group-name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
}

.notification-action-panel-group-name i {
  color: var(--text-muted);
  font-size: 12px;
}

.notification-action-panel-group-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.notification-action-panel-pill {
  font-size: 12px;
  padding: 4px 12px;
  border-radius: 999px;
  border: 1px solid #e2e5e9;
  background: #f6f8fa;
  color: #57606a;
  cursor: pointer;
  transition: 0.15s ease;
}

.notification-action-panel-pill:hover {
  border-color: #a5bed6;
}

.notification-action-panel-pill.is-selected {
  background: #eaf1f8;
  border-color: #a5bed6;
  color: #577696;
}

.notification-action-panel-pill-all { font-weight: 600; }

.notification-action-panel-pill-all.is-selected {
  background: #eaf1f8;
  border-color: #a5bed6;
  color: #577696;
}

.notification-action-panel-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 0;
  color: var(--text-muted);
  font-size: 13px;
  text-align: center;
}

.notification-action-panel-empty i {
  font-size: 28px;
}
</style>

<style>
.dark-theme .notification-action-panel-channel-btn {
  background: #21262d;
  border-color: #30363d;
  color: #8b949e;
}

.dark-theme .notification-action-panel-channel-btn.is-active {
  background: #30363d;
  border-color: #30363d;
  color: #e6edf3;
}

.dark-theme .notification-action-panel-channel-btn.is-active i {
  color: #e6edf3;
}

.dark-theme .notification-action-panel-group-row {
  background: #161b22;
  border-color: #30363d;
}

.dark-theme .notification-action-panel-pill {
  background: #21262d;
  border-color: #30363d;
  color: #8b949e;
}

.dark-theme .notification-action-panel-pill:hover {
  background: #30363d;
  border-color: #30363d;
}

.dark-theme .notification-action-panel-pill.is-selected {
  background: #30363d;
  border-color: #30363d;
  color: #e6edf3;
}

.dark-theme .notification-action-panel-pill-all.is-selected {
  background: #30363d;
  border-color: #30363d;
  color: #e6edf3;
}
</style>
